import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Zap, Target, DollarSign, HeartHandshake, ArrowRight, Users,
  Lightbulb, Heart
} from 'lucide-react';

const About = () => {
  const values = [
    { icon: Zap, title: 'Speed', desc: 'We respect your time. Launch in 48 hours, not 48 days.', color: 'bg-primary-500' },
    { icon: Target, title: 'Quality', desc: 'No compromises on design. Every pixel matters.', color: 'bg-secondary-500' },
    { icon: DollarSign, title: 'Transparency', desc: 'No hidden costs. What you see is what you pay.', color: 'bg-accent-500' },
    { icon: HeartHandshake, title: 'Partnership', desc: 'Your success is our success. We grow together.', color: 'bg-primary-600' }
  ];

  const team = [
    { name: 'Vijay Raghani', role: 'Co-Founder', bio: 'Passionate about digitizing Indian SMBs with modern tech solutions' },
    { name: 'Rajat Mahajan', role: 'Co-Founder', bio: 'Building scalable tech products for Bharat\'s growing businesses' }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="pt-28 pb-20 lg:pt-36 lg:pb-28 bg-hero-gradient relative overflow-hidden" data-testid="about-hero">
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-secondary-200/20 rounded-full blur-3xl" />

        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-2 bg-primary-50 text-primary-600 font-medium rounded-full text-sm mb-6">
                About Us
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold text-neutral-900 mb-6 leading-[1.1]">
                Building Digital Success for{' '}
                <span className="text-gradient-primary">Indian Businesses</span>
              </h1>
              <p className="text-lg text-neutral-500 leading-relaxed">
                MakeMyDukaan was born from a simple observation: while big corporations have massive marketing budgets, small businesses struggle to even get a decent website.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary-200/40 to-secondary-200/40 rounded-3xl blur-2xl" />
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                  alt="Team collaborating"
                  className="relative rounded-2xl shadow-2xl w-full"
                  data-testid="about-hero-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 lg:py-28 bg-white" data-testid="our-story-section">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-neutral-900 mb-8">
                Our Story
              </h2>
              <div className="space-y-5 text-neutral-600 text-lg leading-relaxed">
                <p>
                  Traditional web agencies charge lakhs, take months, and deliver generic WordPress sites. We knew there had to be a better way.
                </p>
                <p>
                  Using modern technology, we've built a system that delivers premium websites in 48 hours at a fraction of traditional cost.
                </p>
                <p>
                  We started with salons and clinics because they need it most. But our vision is bigger: to empower every Indian service business with world-class digital presence.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 lg:py-28 bg-section-alt" data-testid="mission-section">
        <div className="container">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-3xl p-10 md:p-14 text-center">
              <Lightbulb className="w-14 h-14 text-primary-200 mx-auto mb-6" />
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-5">
                Our Mission
              </h2>
              <p className="text-primary-100 text-xl leading-relaxed max-w-2xl mx-auto">
                Make professional web presence accessible to every Indian business, regardless of size or budget.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-white" data-testid="values-section">
        <div className="container">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-neutral-900">Our Values</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                className="card-base text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`value-card-${v.title.toLowerCase()}`}
              >
                <div className={`w-14 h-14 ${v.color} rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg`}>
                  <v.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg text-neutral-900 mb-2">{v.title}</h3>
                <p className="text-neutral-500 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 lg:py-28 bg-section-alt" data-testid="team-section">
        <div className="container">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-primary-50 text-primary-600 font-medium rounded-full text-sm mb-4">
              Our Team
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-neutral-900">
              Meet the Founders
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {team.map((member, i) => (
              <motion.div
                key={member.name}
                className="card-base text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`team-member-${i}`}
              >
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center mx-auto mb-5 shadow-lg">
                  <span className="text-2xl font-heading font-bold text-white">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-xl text-neutral-900">{member.name}</h3>
                <p className="text-primary-500 font-medium text-sm mb-2">{member.role}</p>
                <p className="text-neutral-500 text-sm">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-primary-600 via-primary-500 to-secondary-500" data-testid="about-cta">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Users className="w-14 h-14 text-white/60 mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-5">
              Join Our Growing Family
            </h2>
            <p className="text-white/75 text-lg mb-8 max-w-xl mx-auto">
              Become part of 100+ businesses that trust MakeMyDukaan for their digital journey.
            </p>
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-8 py-4 rounded-xl font-bold text-base hover:bg-neutral-50 transition-all hover:-translate-y-1 shadow-xl inline-flex items-center gap-2"
              data-testid="about-final-cta"
            >
              Start Your Journey
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
